<template>
  <div class="itemPackages" v-if="getItemPackages">
    <DataTable
      class="p-datatable-sm p-datatable-striped p-datatable-gridlines"
      :value="getItemPackages"
      :loading="isLoading"
      :rowHover="true"
      :lazy="true"
      filterDisplay="row"
      :stateKey="tableStateStore.filterName()"
      :filters.sync="tableStateStore.filters"
      :rows.sync="tableStateStore.pagination.pageSize"
      :sortField="tableStateStore.sorting.sortField"
      :sortOrder="tableStateStore.sorting.sortOrder"
      :first="tableStateStore.skip()"
      stateStorage="local"
      :totalRecords="getItemPackageCount"
      :paginator="true"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="
        $t('show') +
          ' {first} ' +
          $t('to') +
          ' {last} ' +
          $t('of') +
          ' {totalRecords} ' +
          $t('records')
      "
      :rowsPerPageOptions="[10, 20, 50, 100]"
      @page="onPage($event)"
      @sort="onSort($event)"
      @filter="onFilter($event)"
      @state-restore="onStateRestore($event)"
      responsiveLayout="scroll"
    >
      <template #header>
        <div class="table-header">
          <div class="table-header-left">
            <h4 class="page-title">{{ $t('itemPackages') }}</h4>
          </div>
          <div class="table-header-right">
            <button class="btn btn-sm btn-inverse" @click.prevent="onRemoveFilters()">
              <i class="fa fa-remove mr-2"></i>
              {{ $t('remove_filters') }}
            </button>

            <button
              v-if="!isReadOnly"
              class="btn btn-sm btn-inverse"
              @click="$emit('create-item-package')"
            >
              <i class="fa fa-plus-circle mr-2"></i>
              {{ $t('itemPackage_create') }}
            </button>
          </div>
        </div>
      </template>

      <template #empty>
        {{ $t('no_data_found') }}
      </template>
      <template #loading>
        {{ $t('loading') }}
      </template>

      <!-- Package Number -->
      <Column
        field="number"
        header="#"
        sortable
        :headerStyle="{ width: '60px' }"
        :bodyStyle="'text-align: center; width: 60px'"
        filterField="number"
      >
        <template #body="slotProps">
          <router-link
            v-if="slotProps.data && slotProps.data.number !== null"
            :to="{
              name: 'ItemPackageEditPage',
              params: { itemPackageNumber: slotProps.data.number },
            }"
          >
            {{ slotProps.data.number.toString().padStart(4, '0') }}
          </router-link>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- Name -->
      <Column
        field="name"
        :header="$t('name')"
        sortable
        filterMatchMode="contains"
        filterField="name"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.name">
            {{ slotProps.data.name }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- Active -->
      <Column
        field="active"
        :header="$t('ACTIVE')"
        sortable
        :headerStyle="{ width: '120px' }"
        :bodyStyle="'text-align: center; width: 120px'"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <b-badge
            v-if="slotProps.data && slotProps.data.active !== undefined"
            :variant="getActiveBadgeColor(slotProps.data.active)"
          >
            {{ slotProps.data.active ? $t('YES') : $t('NO') }}
          </b-badge>
        </template>
        <template #filter>
          <MultiSelect
            :value="tableStateStore.customFilters.active"
            :options="activeOptions"
            @input="onActiveFilter"
            optionLabel="label"
            :placeholder="$t('ACTIVE')"
            display="chip"
            :style="{ 'max-width': '120px' }"
          >
            <template #option="slotProps">
              <b-badge :variant="getActiveBadgeColor(slotProps.option.value)">
                {{ $t(slotProps.option.label) }}
              </b-badge>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <!-- Actions -->
      <Column
        :header="$t('actions')"
        :headerStyle="{ width: '100px' }"
        :bodyStyle="'text-align: center; width: 100px'"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <button
            class="btn"
            @click="$emit('edit-item-package', slotProps.data)"
            title="Edit Package"
          >
            <i class="link-icon fa fa-lg fa-edit"></i>
          </button>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import { getYesNoStateColor } from '@/helpers/colors';

export default {
  name: 'ItemPackagesTable',
  components: {
    DataTable,
    InputText,
    Column,
    MultiSelect,
  },

  props: {
    tableStatePrefix: {
      type: String,
      default: 'itemPackages',
    },
    /** Viewer on the view item-packages: lists, cannot create. */
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      itemPackageTableStateConfig: {
        name: this.tableStatePrefix,
        filters: {
          number: { value: null },
          name: { value: null },
        },
        customFilters: {
          active: [],
        },
        sorting: { sortField: 'number', sortOrder: -1 },
      },
      defaultFilters: {
        number: { value: null },
        name: { value: null },
      },
      defaultCustomFilters: {
        active: [],
      },
      activeOptions: [
        { value: true, label: 'YES' },
        { value: false, label: 'NO' },
      ],
    };
  },

  computed: {
    ...mapGetters(['getItemPackages', 'getItemPackageCount']),
    ...mapGetters(['isLoading', 'getTableState']),

    tableStateStore() {
      return this.getTableState(this.itemPackageTableStateConfig.name);
    },
  },

  methods: {
    ...mapActions(['initTableState', 'fetchItemPackagesPaginated']),

    /**
     * Load remote table data
     */
    async loadTableData() {
      const filters = { ...this.tableStateStore.filters, ...this.tableStateStore.customFilters };

      await this.fetchItemPackagesPaginated({
        pagination: {
          skip: this.tableStateStore.skip(),
          pageSize: this.tableStateStore.pagination.pageSize,
        },
        sorting: this.tableStateStore.sorting,
        filters: filters,
      });
    },

    /**
     * Load more results from server or cache on pagination click
     */
    async onPage(event) {
      this.tableStateStore.pagination = { page: event.page, pageSize: event.rows };
      this.loadTableData();
    },

    /**
     * Load results from server/cache on sort
     */
    async onSort(event) {
      this.tableStateStore.sorting = { sortField: event.sortField, sortOrder: event.sortOrder };
      this.loadTableData();
    },

    /**
     * Load results from server/cache on filter
     */
    async onFilter(event) {
      this.loadTableData();
    },

    /**
     * Trigger when state is loaded from local storage
     */
    async onStateRestore(event) {
      this.tableStateStore.restore(event);
    },

    /**
     * Filter by active status
     */
    async onActiveFilter(value) {
      const filteredActive = {
        active: value, // MultiSelect returns array of selected option objects
      };
      this.tableStateStore.setCustomFilters(filteredActive);
      await this.loadTableData();
    },

    /**
     * Get badge color for active/inactive state
     */
    getActiveBadgeColor(active) {
      return active ? 'success' : 'danger';
    },

    /**
     * Reset filter settings
     */
    async onRemoveFilters(event) {
      this.tableStateStore.reset();
      await this.loadTableData();
    },
  },

  async created() {
    await this.initTableState(this.itemPackageTableStateConfig);
  },

  async mounted() {
    await this.loadTableData();
  },
};
</script>

<style lang="scss">
.p-column-filter-clear-button {
  display: none;
  width: 0;
}

.p-column-filter-menu-button {
  display: none;
  width: 0;
}

.table-header {
  display: flex;
  justify-content: space-between;
}

.table-header-left {
  display: flex;
  justify-content: flex-start;
}

.table-header-right {
  display: flex;
  justify-content: flex-end;
  column-gap: 1em;
}

/** Fix Dropdown overlay and table height */
.p-datatable-responsive-scroll > .p-datatable-wrapper {
  min-height: 37rem;
}
</style>
